import convert from "convert-units";
import _ from "lodash";
import type { ITheme } from "native-base";
import React from "react";
import { Text, View } from "react-native";

import type {
  GeneratedRecipeMealIngredient,
  GroceryListItemUpdate as GroceryListItem,
  Ingredient,
  SuggestedServing,
  User,
} from "../services/backendTypes";
import {
  formatNumberAsDecimal,
  formatNumberAsFractionOrDecimal,
  formatNumberAsWholeNumberForGroceryList,
} from "./generalHelpers";

export function getIngredientInWeightStringForDisplay(
  ingredientInGrams: string,
  isServingInGrams: boolean,
  shouldWeUseImperial: boolean = false
): string {
  if (isServingInGrams) {
    // Serving already in grams so we don't want to display it again
    return "";
  }

  if (!ingredientInGrams) {
    return "";
  }

  if (shouldWeUseImperial) {
    return "";
  }

  return ingredientInGrams;
}

/**
 *
 * @param suggestedServing
 * @param ingredient
 * @returns `{
    servingDescription: '1 tbsp',
    servingInGrams: '17g',
    nonEditableDescription: '2 tbsp',
    ingredientInGrams: '34g'
  }`
 */
export function getServingDescriptionText(
  suggestedServing: SuggestedServing,
  ingredient?: Ingredient | GeneratedRecipeMealIngredient,
  shouldWeUseImperial: boolean = false
): {
  servingInGrams: string;
  nonEditableDescription: string;
  ingredientInGrams: string | undefined;
  isServingInGrams: boolean;
  unit: string;
} {
  let servingDescription = suggestedServing ? suggestedServing.serving_description : "";

  const isServingInGrams = servingDescription.endsWith(" g");

  if (ingredient && servingDescription === ingredient.name) {
    servingDescription = "";
  }

  const servingInGrams = suggestedServing.one_serving_in_metric_value
    ? `${suggestedServing.one_serving_in_metric_value}g`
    : "";

  // NOTE: This was previous code for a non-editable description. It is here if it is of use for other functionality
  const quantityOfServingDescription = parseFloat(servingDescription);
  const isQuantityOfServingDescriptionANumber = !Number.isNaN(quantityOfServingDescription);

  let unit = _(servingDescription).split(" ").tail().join(" ");

  // https://regex101.com/r/BStwwN/1
  const GRAMS_SERVING_DESCRIPTION_REGEX = /[0-9]+g$/;
  if (GRAMS_SERVING_DESCRIPTION_REGEX.test(servingDescription)) {
    unit = "g";
  }

  let nonEditableDescription = servingDescription;
  let ingredientInGrams;
  if (ingredient) {
    if (suggestedServing.one_serving_in_metric_value) {
      ingredientInGrams = `${formatNumberAsDecimal(
        ingredient.quantity * suggestedServing.one_serving_in_metric_value,
        2
      )}g`;
    }

    const quantityForDisplay = parseFloat(formatNumberAsDecimal(ingredient.quantity * quantityOfServingDescription, 2));

    const formattedQuantity = formatNumberAsFractionOrDecimal(ingredient.quantity);
    const formattedQuantityWithServing = isQuantityOfServingDescriptionANumber
      ? formatNumberAsFractionOrDecimal(ingredient.quantity * quantityOfServingDescription)
      : formattedQuantity;

    nonEditableDescription = !isQuantityOfServingDescriptionANumber
      ? `${formattedQuantity} ${_.truncate(servingDescription)}`
      : `${formattedQuantityWithServing} ${unit}`;

    // NOTE: 0.66 is rounded to 0.67 so we add it here
    const normalNumberFactors = [1, 0.5, 0.25, 0.33, 0.66, 0.67];
    const quantityForDisplayIsNotAUsualNumber = !_.some(
      normalNumberFactors,
      (factor) => quantityForDisplay % factor !== 0
    );
    const quantityForDisplayIsNotAWholeNumber = quantityForDisplay % 1 !== 0;

    if (quantityForDisplayIsNotAWholeNumber && quantityForDisplayIsNotAUsualNumber && ingredientInGrams) {
      nonEditableDescription = ingredientInGrams;
    }

    const ingredientInWeightStringForDisplay = getIngredientInWeightStringForDisplay(
      ingredientInGrams || "",
      isServingInGrams,
      shouldWeUseImperial
    );

    if (ingredientInWeightStringForDisplay) {
      nonEditableDescription = `${nonEditableDescription} (${ingredientInWeightStringForDisplay})`;
    }
  }

  return { servingInGrams, nonEditableDescription, ingredientInGrams, isServingInGrams, unit };
}

export function shouldWeUseImperialForThisUser(user: User): boolean {
  if (user.intake?.uses_imperial_measurements_for_recipes || user.is_coach) {
    return Boolean(user.intake?.uses_imperial_measurements_for_recipes);
  }

  if (!user.coach) {
    return Boolean(user.intake?.uses_imperial_measurements_for_recipes);
  }

  return Boolean(user.coach.uses_imperial_measurements);
}

export function createIngredientDescriptionComponent(
  ingredient: Ingredient | GeneratedRecipeMealIngredient,
  theme: ITheme,
  user: User | null
): JSX.Element {
  if (!ingredient.suggested_serving) {
    console.warn(
      `Ingredient ${
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        ingredient.id || "GENERATED_RECIPE_MEAL_INGREDIENT"
      } does not have a suggested_serving. This is unexpected and should be investigated.`
    );

    return <></>;
  }

  const { unit, ingredientInGrams, isServingInGrams } = getServingDescriptionText(
    ingredient.suggested_serving,
    ingredient
  );
  const doNotShowIngredientInGrams = user ? shouldWeUseImperialForThisUser(user) : false;

  return (
    <View style={{ flexDirection: "row" }}>
      {/* NOTE: This empty view is required to make the two elements stack vertically */}
      <View>
        {!isServingInGrams ? (
          <Text style={{ color: theme.colors.gray["500"] }} testID="servingDescription">
            {_.truncate(unit)}{" "}
          </Text>
        ) : null}
        {ingredientInGrams && !doNotShowIngredientInGrams ? (
          <Text style={{ color: theme.colors.gray["500"] }} testID="ingredientInGrams">
            {` (${ingredientInGrams})`}
          </Text>
        ) : null}
      </View>
    </View>
  );
}

export function groceryListItemIsLiquid(suggestedServing: SuggestedServing): boolean {
  /**
   * NOTE: The backend converts all liquid measurements to ml (because most Foods don't have imperial SuggestedServings)
   */

  return suggestedServing.measurement_description === "ml";
}

export function formatGroceryListItemQuantity(quantity: number): string {
  const formatted = formatNumberAsWholeNumberForGroceryList(quantity);

  if (formatted === "0") {
    return "1";
  }

  return formatted;
}

/**
 * Rounds to common cooking fractions
 */
function roundToCommonFraction(value: number): number {
  const fractions = [0, 0.25, 0.33, 0.5, 0.67, 0.75, 1];
  const wholePart = Math.floor(value);
  const fractionalPart = value - wholePart;

  // Find closest fraction
  const closestFraction = fractions.reduce((prev, curr) =>
    Math.abs(curr - fractionalPart) < Math.abs(prev - fractionalPart) ? curr : prev
  );

  // If we're very close to the next whole number, round up
  if (closestFraction === 1) {
    return wholePart + 1;
  }

  return wholePart + closestFraction;
}

/**
 * Smart rounding function that rounds to appropriate precision based on unit
 */
function smartRound(value: number, unit: string): number {
  switch (unit) {
    case "tsp":
    case "tbsp":
      // Round to nearest ¼
      return Math.round(value * 4) / 4;

    case "oz":
    case "fl oz":
      // Round to nearest 0.5 for small amounts, whole for large
      if (value < 8) return Math.round(value * 2) / 2;
      return Math.round(value);

    case "cup":
    case "cups":
      // Round to common fractions: ¼, ⅓, ½, ⅔, ¾
      return roundToCommonFraction(value);

    case "lb":
      // Round to nearest 0.25
      return Math.round(value * 4) / 4;

    default:
      return Math.ceil(value); // Fallback to current behavior
  }
}

/**
 * Formats volume measurements with appropriate imperial units
 * ml → tsp, tbsp, fl oz, cups, pints, quarts, gallons
 */
function formatImperialVolume(ml: number): string {
  // Less than 5ml - use teaspoons
  if (ml < 5) {
    const tsp = ml / 4.92892; // 1 tsp = 4.92892 ml
    const rounded = smartRound(tsp, "tsp");
    if (rounded === 0) return "¼ tsp";
    return `${formatNumberAsFractionOrDecimal(rounded)} tsp`;
  }

  // 5-15ml - use teaspoons
  if (ml <= 15) {
    const tsp = ml / 4.92892;
    const rounded = smartRound(tsp, "tsp");
    return `${formatNumberAsFractionOrDecimal(rounded)} tsp`;
  }

  // 15-30ml - use tablespoons
  if (ml <= 30) {
    const tbsp = ml / 14.7868; // 1 tbsp = 14.7868 ml
    const rounded = smartRound(tbsp, "tbsp");
    return `${formatNumberAsFractionOrDecimal(rounded)} tbsp`;
  }

  // 30-60ml - use fluid ounces
  if (ml <= 60) {
    const flOz = convert(ml).from("ml").to("fl-oz");
    const rounded = smartRound(flOz, "fl oz");
    return `${formatNumberAsFractionOrDecimal(rounded)} fl oz`;
  }

  // 60-240ml - prefer cups for common amounts, fl oz for others
  if (ml <= 240) {
    const cups = ml / 236.588; // 1 cup = 236.588 ml
    const flOz = convert(ml).from("ml").to("fl-oz");

    // If it's close to a nice cup fraction, use cups
    if (Math.abs(cups - 0.25) < 0.05) return "¼ cup";
    if (Math.abs(cups - 0.33) < 0.05) return "⅓ cup";
    if (Math.abs(cups - 0.5) < 0.05) return "½ cup";
    if (Math.abs(cups - 0.67) < 0.05) return "⅔ cup";
    if (Math.abs(cups - 0.75) < 0.05) return "¾ cup";
    if (Math.abs(cups - 1) < 0.05) return "1 cup";

    // Otherwise use fl oz
    const rounded = smartRound(flOz, "fl oz");
    return `${formatNumberAsFractionOrDecimal(rounded)} fl oz`;
  }

  // 240-960ml - use cups
  if (ml <= 960) {
    const cups = ml / 236.588;
    const rounded = smartRound(cups, "cups");
    const unit = rounded === 1 ? "cup" : "cups";
    return `${formatNumberAsFractionOrDecimal(rounded)} ${unit}`;
  }

  // 960-3785ml - use cups or quarts
  if (ml <= 3785) {
    const quarts = ml / 946.353; // 1 quart = 946.353 ml
    if (quarts < 2) {
      const cups = ml / 236.588;
      const rounded = smartRound(cups, "cups");
      return `${formatNumberAsFractionOrDecimal(rounded)} cups`;
    }
    const rounded = smartRound(quarts, "quarts");
    const unit = rounded === 1 ? "quart" : "quarts";
    return `${formatNumberAsFractionOrDecimal(rounded)} ${unit}`;
  }

  // > 3785ml - use gallons
  const gallons = ml / 3785.41; // 1 gallon = 3785.41 ml
  const rounded = smartRound(gallons, "gallons");
  const unit = rounded === 1 ? "gallon" : "gallons";
  return `${formatNumberAsFractionOrDecimal(rounded)} ${unit}`;
}

/**
 * Formats mass measurements with appropriate imperial units
 * g → tsp/tbsp (for powders), oz, lb
 */
function formatImperialMass(grams: number): string {
  // Less than 5g - use teaspoons for powders/spices
  if (grams < 5) {
    // Approximate conversion for common spices (varies by density)
    const tsp = grams / 2.5; // Rough average for spices
    const rounded = smartRound(tsp, "tsp");
    if (rounded === 0) return "¼ tsp";
    return `${formatNumberAsFractionOrDecimal(rounded)} tsp`;
  }

  // 5-28g - use tablespoons or ounces
  if (grams <= 28) {
    const oz = convert(grams).from("g").to("oz");

    // If it's a small amount that would be better expressed as tbsp
    if (grams <= 14) {
      const tbsp = grams / 7; // Rough average for cooking ingredients
      const rounded = smartRound(tbsp, "tbsp");
      return `${formatNumberAsFractionOrDecimal(rounded)} tbsp`;
    }

    const rounded = smartRound(oz, "oz");
    return `${formatNumberAsFractionOrDecimal(rounded)} oz`;
  }

  // 28-454g - use ounces
  if (grams <= 454) {
    const oz = convert(grams).from("g").to("oz");
    const rounded = smartRound(oz, "oz");
    return `${formatNumberAsFractionOrDecimal(rounded)} oz`;
  }

  // > 454g - use pounds and ounces
  const totalOz = convert(grams).from("g").to("oz");
  const pounds = Math.floor(totalOz / 16);
  const remainingOz = totalOz - pounds * 16;

  if (remainingOz < 0.5) {
    // Just show pounds
    return `${pounds} ${pounds === 1 ? "lb" : "lbs"}`;
  }
  if (remainingOz >= 15.5) {
    // Round up to next pound
    return `${pounds + 1} ${pounds + 1 === 1 ? "lb" : "lbs"}`;
  }
  // Show mixed units
  const roundedOz = smartRound(remainingOz, "oz");
  return `${pounds} lb ${formatNumberAsFractionOrDecimal(roundedOz)} oz`;
}

/**
 * Formats metric weights with better precision
 */
function formatMetricWeight(grams: number): string {
  if (grams < 5) {
    // Show exact for very small amounts
    return `${formatNumberAsDecimal(grams, 1)}g`;
  }

  if (grams < 1000) {
    // Round to nearest 5g for medium amounts
    const rounded = Math.round(grams / 5) * 5;
    return `${rounded}g`;
  }

  // Use kg for large amounts
  const kg = grams / 1000;
  return `${formatNumberAsDecimal(kg, 1)}kg`;
}

export const getGroceryListItemInWeight = (item: GroceryListItem, shouldUseImperial = false): string => {
  // Eg, eggs have no "unit"
  const itemWithoutUnit = item.name === item.suggested_serving.measurement_description;
  if (itemWithoutUnit || !item.suggested_serving.one_serving_in_metric_value) return String(item.quantity);

  const itemTotalInMetric = item.quantity * item.suggested_serving.one_serving_in_metric_value;

  if (shouldUseImperial) {
    if (groceryListItemIsLiquid(item.suggested_serving)) {
      return formatImperialVolume(itemTotalInMetric);
    }
    return formatImperialMass(itemTotalInMetric);
  }

  return formatMetricWeight(itemTotalInMetric);
};

export function isInstagramExternalSource(url: string): boolean {
  return url.includes("instagram.com");
}
